import {
  Button,
  Checkbox,
  Divider,
  LoadingOverlay,
  Modal,
  Select,
} from "@mantine/core"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { Controller, useForm } from "react-hook-form"
import React, { useEffect, useState } from "react"
import { DatePickerInput } from "@mantine/dates"
import * as FileSaver from "file-saver"
import { IconFileExport } from "@tabler/icons-react"
import { useTranslation } from "react-i18next"
import { CollectionQuery, Filter } from "../../../models/collection.model"
import { notification } from "../../../shared/component/notification/utility/notification"
import { useLazyGetTenantsOfLeaseQuery } from "../../lease/store/lease.query"
import { Tenant } from "../../../models/tenant.model"
import { debounce } from "lodash"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"

const ExportPaymentModalComponent = (props: any) => {
  const { t } = useTranslation()
  const { opened, open, close, filteringVariables, exportFunction, filename } =
    props
  const [dateRangeValue, setDateRangeValue] = useState<
    [Date | null, Date | null]
  >([null, null])
  const [userInfo, userId, ownerInfo] = useCurrentUser()

  const [selectedTenant, setSelectedTenant] = useState<string | null>(null)
  const [filterValue, setFilterValue] = useState<string[]>([])
  const [dateFilter, setDateFilterModel] = useState<Filter[]>([])
  const [collectionQuery, setCollectionQuery] = useState<CollectionQuery>({
    skip: 0,
    orderBy: [{ field: "createdAt", direction: "asc" }],
    includes: ["tenant", "lease", "owner", "lease.property", "lease.unit"],
  })
  const [tenantsCollection, setTenantsCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
  })
  const [tenantSearch, setTenantSearch] = useState<string | null>(null)

  const [exportLoading, setExportLoading] = useState<boolean>(false)
  const [getTenants, tenants] = useLazyGetTenantsOfLeaseQuery()

  const filterQuery = (data: string[]) => {
    let filterQueryValue: any[] = []
    const filterMap: { [key: string]: any[] } = {}
    data.forEach((item) => {
      filterMap[JSON.parse(item)?.field] = data.filter(
        (query) => JSON.parse(query)?.field === JSON.parse(item).field,
      )
    })
    // constructs the filter query into array form the grouped object
    Object.keys(filterMap).forEach((key) => {
      filterQueryValue = [
        ...filterQueryValue,
        filterMap[key].map((item) => JSON.parse(item)),
      ]
    })
    return filterQueryValue
  }

  useEffect(() => {
    if (dateRangeValue?.length > 0) {
      let date = []
      if (dateRangeValue?.[0] && dateRangeValue?.[1]) {
        date.push({
          field: "createdAt",
          value: [
            dateRangeValue?.[0]?.toISOString(),
            dateRangeValue?.[1]?.toISOString(),
          ],
          operator: "between",
        })
      }

      setDateFilterModel(date)
    }
  }, [dateRangeValue])

  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getTenants(tenantsCollection)
    } else {
      getTenants({
        ...tenantsCollection,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [tenantsCollection])

  useEffect(() => {
    if (tenantSearch) {
      if (userInfo?.role?.key == "super_admin") {
        getTenants({
          ...tenantsCollection,
          filter: [
            [
              { field: "name", operator: "like", value: tenantSearch },
              { field: "phoneNumber", operator: "like", value: tenantSearch },
            ],
          ],
        })
      } else {
        getTenants({
          ...tenantsCollection,
          filter: [
            [
              { field: "name", operator: "like", value: tenantSearch },
              { field: "phoneNumber", operator: "like", value: tenantSearch },
            ],
            [
              {
                field: "ownerId",
                operator: "=",
                value: ownerInfo?.parentOwnerId,
              },
            ],
          ],
        })
      }
    }
  }, [tenantSearch])

  useEffect(() => {
    const filter = filterQuery(filterValue)
    // const transportFilter = filterQuery(transportFilterValue);
    if (dateFilter.length > 0) {
      setCollectionQuery({
        ...collectionQuery,
        filter: [...filter, dateFilter],
      })
    } else {
      setCollectionQuery({
        ...collectionQuery,
        filter: [...filter],
      })
    }
  }, [filterValue, dateFilter, setCollectionQuery])

  const onExport = () => {
    setExportLoading(true)
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset-UTF-8;"

    let filter: Filter[][] = []

    if (selectedTenant) {
      filter.push([
        { field: "tenantId", operator: "=", value: selectedTenant as string },
      ])
    }
    exportFunction({ ...collectionQuery, filter: filter })
      .then((result: any) => {
        if (result) {
          const data = new Blob([result], { type: fileType })
          FileSaver.saveAs(data, filename)
          setExportLoading(false)
        }
      })
      .catch((e: any) => {
        setExportLoading(false)
        notification(
          "error",
          e?.message
            ? (e?.message as string)
            : "Unable to export the data. Please try again",
        )
      })
  }

  function _parseTenants() {
    let data: { label: string; value: string }[] = []

    tenants?.data?.data?.map((tenant: Tenant) => {
      data.push({
        label: `${tenant?.name as string} (${tenant?.phoneNumber as string})`,
        value: tenant?.id as string,
      })
    })

    return data
  }
  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        className="my-modal-content"
        withCloseButton={true}
        title={t("export")}
        padding={20}
        size={"lg"}
      >
        <div className="w-full">
          <div className="flex mb-4 w-1/2">
            <DatePickerInput
              className="w-full text-sm"
              dropdownType="modal"
              type="range"
              //@ts-ignore
              placeholder="Pick date range"
              allowSingleDateInRange
              value={dateRangeValue}
              onChange={setDateRangeValue}
              maw={400}
            />
          </div>

          <Divider label={t("and")} labelPosition="center" my={"xs"} />

          <div className="flex mb-4 w-1/2">
            <Select
              classNames={{ label: "dark:text-white" }}
              className="w-full"
              onKeyDown={debounce(
                (event: any) => setTenantSearch(event?.target?.value),
                1000,
              )}
              searchable
              clearable
              // withAsterisk
              // error={
              //     errors.tenantId &&
              //     `${errors?.tenantId?.message}`
              // }
              label={t("tenant", { count: 1 })}
              placeholder={t("tenant", { count: 1 })}
              data={_parseTenants()}
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e)}
              // {...field}
            />
          </div>
          <Divider label={t("and")} labelPosition="center" my={"xs"} />

          <div className="flex-col space-y-2">
            <LoadingOverlay visible={false} overlayBlur={2} />

            <Checkbox.Group
              size="xs"
              className="flex-col  w-full justify-start "
              withAsterisk
              defaultValue={filterValue}
              value={filterValue}
              onChange={(data: any) => {
                setFilterValue(data)
              }}
            >
              {filteringVariables?.map((item: any, idx: number) => (
                <div key={idx}>
                  <div className="flex space-x-3  items-center">
                    {item.map((filter: any, index: number) => (
                      <Checkbox
                        key={index}
                        value={JSON.stringify(filter)}
                        label={filter.name}
                      />
                    ))}
                  </div>
                  {filteringVariables?.length !== idx + 1 && (
                    <Divider
                      label={t("and")}
                      labelPosition="center"
                      my={"xs"}
                    />
                  )}
                </div>
              ))}
            </Checkbox.Group>
          </div>

          <div className="mt-4  w-full flex justify-end">
            <Button
              loading={exportLoading}
              onClick={() => onExport()}
              size="xs"
              leftIcon={<IconFileExport />}
              className="flex bg-primary text-white"
            >
              Export
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ExportPaymentModalComponent
